import React, { useState } from 'react'
import Modal from './Modal'
import Card from './Card'
import Cards from './Cards'
import Note from './Note'
import { NoteType } from '../enums/NoteType'
import LoadingSpinnerOverlay from './LoadingSpinnerOverlay'
import { CompanyDto } from '../api/company/models/CompanyDto'
import companyApi from '../api/company/CompanyApi'
import Button from './Button'
import CompanyUi from './CompanyUi'

interface SearchCompaniesModalProps {
    onClose: () => void
    onSelect: (company: CompanyDto) => Promise<void> | void
    selectedCompanySeedIds: string[]
    externalIsLoading?: boolean
}

function SearchCompaniesModal({
    onClose,
    onSelect,
    selectedCompanySeedIds,
    externalIsLoading = false,
}: SearchCompaniesModalProps): React.JSX.Element {
    const [searchTerm, setSearchTerm] = useState('')
    const [foundCompanies, setFoundCompanies] = useState<CompanyDto[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [errMsg, setErrMsg] = useState('')

    async function handleSearch() {
        setIsLoading(true)
        setErrMsg('')
        try {
            const searchResults = await companyApi.searchCompanies(searchTerm)

            // Filter out companies that are already linked
            searchResults.companies = searchResults.companies.filter(
                (company) => !selectedCompanySeedIds.includes(company.id),
            )

            setFoundCompanies(searchResults.companies)
        } catch (error) {
            setErrMsg('Error searching companies')
        } finally {
            setIsLoading(false)
        }
    }

    // Combine internal and external loading states
    const loading = isLoading || externalIsLoading

    return (
        <Modal onClose={onClose} title="Select a Company" size="large">
            <LoadingSpinnerOverlay show={loading}>
                <div className="flex flex-col space-y-4">
                    {/* Search Input & Button */}
                    <div className="flex">
                        <input
                            type="text"
                            className="form-input flex-grow"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch()
                                }
                            }}
                        />
                        <Button
                            style="primary"
                            type="button"
                            onClick={handleSearch}
                            classes="ml-4"
                        >
                            Search
                        </Button>
                    </div>

                    {/* Error or No Results Note */}
                    {errMsg ? (
                        <Note type={NoteType.ERROR}>{errMsg}</Note>
                    ) : !loading &&
                      searchTerm &&
                      foundCompanies.length === 0 ? (
                        <Note type={NoteType.INFO}>No companies found.</Note>
                    ) : null}

                    {/* Company Results */}
                    {foundCompanies.length > 0 && (
                        <Cards>
                            {foundCompanies
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((company, index) => (
                                    <Card
                                        key={`company-${index}`}
                                        classes="w-[220px] bg-white"
                                        showTopSection={true}
                                        topSectionClassNames="h-[70px]"
                                        footer={
                                            <div className="flex gap-2">
                                                <Button
                                                    style="secondary"
                                                    type="button"
                                                    classes="mt-4"
                                                    size="small"
                                                    onClick={() =>
                                                        onSelect(company)
                                                    }
                                                >
                                                    Select
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <div className="">
                                            <CompanyUi company={company} />
                                        </div>
                                    </Card>
                                ))}
                        </Cards>
                    )}
                </div>
            </LoadingSpinnerOverlay>
        </Modal>
    )
}

export default SearchCompaniesModal
